import { isNil } from 'lodash/fp';
import { ChangeEvent, ChangeEventHandler, Component } from 'react';
import { connect } from 'react-redux';
import { change, clearFields, formValueSelector } from 'redux-form';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { ApplicationState } from '@mews-commander/types';
import { DateTimeLocalizationInfo, TimeService } from '@mews/framework';
import { CompoundField, StructuredDate } from '@optimus-web/core';
import DateStringPickerField from './DateStringPickerField';
import TimeStringPickerField from './TimeStringPickerField';

const ZERO_TIME = TimeService.parse('00:00:00');
const TIME_FIELD_NAME = 'time';
const DATE_FIELD_NAME = 'date';

interface StateProps {
    time?: string | null;
    dateTimeLocalizationInfo: DateTimeLocalizationInfo;
}

interface DispatchProps {
    setTime: (time: string | null) => void;
    resetDateTime: () => void;
}

interface OwnProps {
    form: string;
    name?: string;
    label?: React.ReactNode;
    minDate?: StructuredDate | null;
    maxDate?: StructuredDate | null;
    required?: boolean;
    allowDateReset?: boolean;
    disabled?: boolean;
}

const getDateFieldName = (name: string | null | undefined) => (name ? `${name}.${DATE_FIELD_NAME}` : DATE_FIELD_NAME);
const getTimeFieldName = (name: string | null | undefined) => (name ? `${name}.${TIME_FIELD_NAME}` : TIME_FIELD_NAME);
class DateTimePickerField extends Component<OwnProps & StateProps & DispatchProps> {
    componentWillUnmount() {
        this.props.resetDateTime();
    }

    handleDateChange = (e: ChangeEvent<HTMLInputElement>, value: string) => {
        const { time, setTime, dateTimeLocalizationInfo } = this.props;
        if (isNil(time)) {
            const zeroTimeString = TimeService.toShortString(ZERO_TIME, dateTimeLocalizationInfo);
            setTime(zeroTimeString);
        }
        if (value === '') {
            setTime(null);
        }
    };

    render() {
        const { label, minDate, required, maxDate, allowDateReset, disabled, name } = this.props;

        return (
            <CompoundField label={label} required={required} name={name || ''}>
                <DateStringPickerField
                    minDate={minDate}
                    maxDate={maxDate}
                    name={getDateFieldName(name)}
                    onChange={this.handleDateChange as ChangeEventHandler<HTMLInputElement>}
                    showMonthSelector={false}
                    allowDateReset={allowDateReset}
                    disabled={disabled}
                />
                <TimeStringPickerField name={getTimeFieldName(name)} disabled={disabled} />
            </CompoundField>
        );
    }
}

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState, { form, name }) => ({
        time: formValueSelector(form)(state, getTimeFieldName(name)),
        dateTimeLocalizationInfo: getCurrentDateTimeLocalizationInfo(state),
    }),
    (dispatch, { form, name }) => ({
        setTime: (time: string | null) => dispatch(change(form, getTimeFieldName(name), time)),
        resetDateTime: () => dispatch(clearFields(form, false, false, getDateFieldName(name), getTimeFieldName(name))),
    })
)(DateTimePickerField);
